import { mapBladeTypeNames } from 'src/actuators/constants';
import { detectTargetUnit } from 'src/actuators/detectTargetUnit';
import { ActuatorTypes } from 'src/actuators/user-input/calculations/constants';
import { AirflowEnum, DamperType, RoundDamperBlades, UserInput } from 'src/actuators/user-input/types';
import { LocalesEnum, RegionsEnum } from 'src/generated/operations';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { ACTUATORS_DECIMAL_DIGITS, MESSAGES } from 'src/modules/common/constants';
import { numberFormatter } from 'src/utils/numberFormatter';
import { Units } from 'uom-units';

type Props = {
    region: RegionsEnum;
    state: UserInput;
    locale: LocalesEnum;
    selectedActuatorType: ActuatorTypes;
}

export const getActuatorBreadcrumbValues = ({ region, state, locale, selectedActuatorType }: Props) => {
    const damperLabel = <I18n messageId={MESSAGES.DamperType}/>;
    const damperValue = state.damper === DamperType.Rectangular
        ? <I18n messageId={MESSAGES.DamperRectangular}/>
        : <I18n messageId={MESSAGES.DamperRound}/>;

    const areaLabel = <I18n messageId={MESSAGES.DamperArea}/>;
    const areaValue = state.areaOrDiameter && numberFormatter(state.areaOrDiameter, locale) ? (
        <>{numberFormatter(state.areaOrDiameter, locale)} <I18n
            messageId={region === RegionsEnum.Am
                ? Units.SquareFeet.name
                : Units.SquareMeter.name}
        />
        </>
    ) : null;

    const diameterLabel = <I18n messageId={MESSAGES.DamperDiameter}/>;
    const diameterValue = state.areaOrDiameter && numberFormatter(state.areaOrDiameter, locale) ? (
        <>
            {numberFormatter(state.areaOrDiameter, locale)} <I18n
                messageId={region === RegionsEnum.Am
                    ? Units.SquareFeet.name
                    : Units.Millimeter.name}
            />
        </>
    ) : null;

    const bladeOrRoundDumperLabel = <I18n messageId={MESSAGES.DamperBladeType}/>;
    const bladeValue = state.blade !== null ? (<I18n messageId={mapBladeTypeNames[state.blade]}/>) : null;
    const roundDumperValue = state.roundDumper !== null ? (
        <I18n
            messageId={state.roundDumper === RoundDamperBlades.BladeWithSeals
                ? MESSAGES.BladeWithSeals : MESSAGES.BladeWithoutSeals}
        />
    ) : null;

    const airflowLabel = <I18n messageId={MESSAGES.actuatorsUserInputAirflow}/>;
    const airflowMapping = {
        [AirflowEnum.low]: <I18n messageId={MESSAGES.AirflowLow}/>,
        [AirflowEnum.mid]: <I18n messageId={MESSAGES.AirflowMid}/>,
        [AirflowEnum.high]: <I18n messageId={MESSAGES.AirflowHigh}/>,
    };
    const airflowValue = state.air !== null ? airflowMapping[state.air] : null;

    const targetUnit = detectTargetUnit(selectedActuatorType, region);
    const damperRequiredLabel = (
        <I18n
            messageId={selectedActuatorType === ActuatorTypes.Linear
                ? MESSAGES.DamperRequiredForce : MESSAGES.DamperRequiredTorque}
        />
    );
    const damperRequired = state.result && numberFormatter(
        state.result,
        locale,
        ACTUATORS_DECIMAL_DIGITS,
    )
        ? (
            <>
                {numberFormatter(
                    state.result,
                    locale,
                    ACTUATORS_DECIMAL_DIGITS,
                )} <I18n
                    messageId={targetUnit.name}
                />
            </>
        ) : null;

    const safetyLabel = <I18n messageId={MESSAGES.DamperSafetyFactor}/>;
    const safetyValue = state.safety && numberFormatter(state.safety, locale) && region !== RegionsEnum.Am ? (
        <>
            {numberFormatter(state.safety, locale)}<I18n messageId={Units.Percent.name}/>
        </>
    ) : null;

    return {
        damper: [damperLabel, damperValue],
        area: [areaLabel, areaValue],
        diameter: [diameterLabel, diameterValue],
        blade: [bladeOrRoundDumperLabel, bladeValue],
        roundDumper: [bladeOrRoundDumperLabel, roundDumperValue],
        airflow: [airflowLabel, airflowValue],
        damperRequired: [damperRequiredLabel, damperRequired],
        safety: [safetyLabel, safetyValue],
    };
};
